import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Droppable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import Joi from 'joi';

import { useCreateCustomTunnelStepMutation } from 'api/main';
import { customTunnelSchemas } from 'schemas';
import { customTunnelSelectors } from 'reducers';
import {
    editingQuestionSet,
    editingStepIdSet,
    newStepQuestionAdded,
    newStepSet,
    newStepUpdated,
} from 'reducers/customTunnel';
import { questionType, quillFormats } from 'utils/constants';
import { validateSchema } from 'utils/helpers';
import { useDialog } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import QuestionItem from './QuestionItem';
import {
    ActionButton,
    CancelButton,
    ConfirmDialog,
    CustomFormLabel,
    CustomTextField,
} from 'components';
import { Box } from '@mui/material';

const schema = {
    title: Joi.string().required().trim().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
};

const AddCustomStep = ({ ppTunnelPhaseId, pmTunnelPhaseId, ppStepOrder, pmStepOrder }) => {
    const dispatch = useDispatch();

    const newStep = useSelector(customTunnelSelectors.getNewStep);
    const editingQuestion = useSelector(customTunnelSelectors.getEditingQuestion);
    const editingAnswerId = useSelector(customTunnelSelectors.getEditingAnswerId);

    const {
        open: confirmSaveOpen,
        onOpen: handleConfirmOpen,
        onClose: handleConfirmClose,
    } = useDialog();

    const [createTunnelStep, { isLoading: createLoading, isSuccess: createSuccess }] =
        useCreateCustomTunnelStepMutation();

    const [errors, setErrors] = useState({ title: '' });

    const handleChange = useCallback(
        key => e => {
            dispatch(newStepUpdated({ key, value: e.target.value }));
        },
        [dispatch]
    );

    const handleChangeSubtitle = useCallback(
        value => {
            dispatch(newStepUpdated({ key: 'subtitle', value }));
        },
        [dispatch]
    );

    const handleAddQuestion = useCallback(() => {
        dispatch(
            newStepQuestionAdded({
                title: '',
                mandatory: true,
                type: questionType.RADIO,
                answers: [],
            })
        );
        dispatch(
            editingQuestionSet({
                value: {
                    title: '',
                    mandatory: true,
                    type: questionType.RADIO,
                    answers: [],
                },
            })
        );
    }, [dispatch]);

    const handleRemoveQuestion = useCallback(
        index => () => {
            let newQuestions = [...newStep.questions];
            newQuestions.splice(index, 1);
            dispatch(newStepUpdated({ key: 'questions', value: newQuestions }));
        },
        [dispatch, newStep.questions]
    );

    const handleCleanupQuestions = useCallback(() => {
        let newQuestions = newStep.questions.filter(q => q.id || q.title);
        dispatch(newStepUpdated({ key: 'questions', value: newQuestions }));
    }, [dispatch, newStep.questions]);

    const handleCancel = useCallback(() => {
        dispatch(newStepSet(null));
        dispatch(editingStepIdSet(null));
    }, [dispatch]);

    const handleSave = useCallback(() => {
        createTunnelStep(
            customTunnelSchemas.createCustomTunnelStep({
                ppStepOrder,
                ppTunnelPhaseId,
                pmStepOrder,
                pmTunnelPhaseId,
                newStep,
            })
        );
    }, [newStep, createTunnelStep, ppStepOrder, ppTunnelPhaseId, pmStepOrder, pmTunnelPhaseId]);

    const handleConfirmSave = useCallback(() => {
        const { newErrors, valid } = validateSchema(schema, newStep);
        if (valid) {
            setErrors({ title: '' });
            if (editingQuestion || editingAnswerId) {
                // show warning
                handleConfirmOpen();
            } else {
                handleSave();
            }
        } else {
            setErrors(newErrors);
        }
    }, [editingAnswerId, editingQuestion, handleConfirmOpen, handleSave, newStep]);

    useEffect(() => {
        if (createSuccess) {
            // clear store data
            dispatch(newStepSet(null));
            dispatch(editingStepIdSet(null));
        }
    }, [createSuccess, dispatch]);

    return (
        <Box
            sx={theme => ({
                border: `3px solid ${theme.palette.primary.main}`,
                borderRadius: '5px',
                marginY: 1,
                padding: theme.spacing(2, 3),
            })}
        >
            <CustomTextField
                label={texts.STEP_TITLE}
                value={newStep.title}
                onChange={handleChange('title')}
                error={errors.title}
            />
            <CustomFormLabel>{texts.STEP_SUBTITLE}</CustomFormLabel>
            <ReactQuill
                className="small"
                value={newStep.subtitle}
                onChange={handleChangeSubtitle}
                newStepats={quillFormats}
            />
            <Box textAlign="center" mb={1}>
                <ActionButton
                    variant="outlined"
                    sx={{ padding: '5px 8px' }}
                    onClick={handleAddQuestion}
                    disabled={!newStep.questions.every(q => q.title)}
                >
                    {texts.ADD_QUESTION}
                </ActionButton>
            </Box>
            <Droppable droppableId="droppable-questions-0" type="question-0">
                {(provided, snapshot) => (
                    <Box
                        paddingY={2}
                        {...provided.droppableProps}
                        sx={{ opacity: snapshot.isDraggingOver ? '.85' : 1 }}
                        ref={provided.innerRef}
                    >
                        {newStep.questions.map((q, idx) => (
                            <QuestionItem
                                key={q.title}
                                index={idx}
                                question={q}
                                onRemove={handleRemoveQuestion(idx)}
                                formCleanup={handleCleanupQuestions}
                            />
                        ))}
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
            <ConfirmDialog
                open={confirmSaveOpen}
                handleClose={handleConfirmClose}
                handleConfirm={handleSave}
                title={texts.CHANGES_NOT_CONFIRMED}
                confirmText={texts.CONFIRMER}
                cancelText={texts.CANCEL}
            >
                {texts.CONFIRM_DISCARD_CHANGES}
            </ConfirmDialog>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} paddingY={2}>
                <ActionButton
                    variant="contained"
                    loading={createLoading}
                    onClick={handleConfirmSave}
                >
                    {texts.SAVE}
                </ActionButton>
                <CancelButton disabled={createLoading} onClick={handleCancel}>
                    {texts.CANCEL}
                </CancelButton>
            </Box>
        </Box>
    );
};

AddCustomStep.propTypes = {
    ppTunnelPhaseId: PropTypes.number,
    pmTunnelPhaseId: PropTypes.number,
    ppStepOrder: PropTypes.number,
    pmStepOrder: PropTypes.number,
};

export default AddCustomStep;
